import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Lock, Phone, Mail, Loader2, CheckCircle, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const AdminSettingsModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('verify'); // 'verify', 'settings'
  const [verificationCode, setVerificationCode] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    admin_pin: '',
    admin_phone: '',
    admin_email: ''
  });
  const [newSettings, setNewSettings] = useState({
    admin_pin: '',
    admin_phone: '',
    admin_email: ''
  });
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      setStep('verify');
      setVerificationCode('');
      setCodeInput('');
      setError('');
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/admin-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setNewSettings({
        admin_pin: '',
        admin_phone: response.data.admin_phone_full || '',
        admin_email: response.data.admin_email || ''
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const sendVerificationCode = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/send-code`, {
        code_type: 'email',
        purpose: 'settings_change'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi du code';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndProceed = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/verify-code`, {
        code: codeInput,
        code_type: 'email',
        purpose: 'settings_change'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVerificationCode(codeInput);
      setStep('settings');
      toast.success('Authentification réussie !');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Code invalide';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const updates = {
        verification_code: verificationCode
      };
      
      if (newSettings.admin_pin && newSettings.admin_pin !== settings.admin_pin) {
        updates.admin_pin = newSettings.admin_pin;
      }
      if (newSettings.admin_phone && newSettings.admin_phone !== settings.admin_phone_full) {
        updates.admin_phone = newSettings.admin_phone;
      }
      if (newSettings.admin_email && newSettings.admin_email !== settings.admin_email) {
        updates.admin_email = newSettings.admin_email;
      }
      
      await axios.put(`${BACKEND_URL}/api/auth/admin-settings`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Paramètres mis à jour avec succès !');
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la sauvegarde';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderVerification = () => (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-amber-600" />
          <span className="font-semibold text-amber-800">Authentification requise</span>
        </div>
        <p className="text-sm text-amber-700">
          Pour modifier les paramètres de sécurité, vous devez d'abord vous authentifier.
        </p>
      </div>

      {!codeInput ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Un code de vérification sera envoyé à votre email : <strong>{settings.admin_email}</strong>
          </p>
          <Button
            onClick={sendVerificationCode}
            disabled={loading}
            className="w-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Envoyer le code de vérification
          </Button>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="verifyCode">Code de vérification</Label>
        <Input
          id="verifyCode"
          type="text"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          className="text-center text-2xl tracking-widest font-mono"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Annuler
        </Button>
        <Button
          onClick={verifyAndProceed}
          disabled={loading || codeInput.length !== 6}
          className="flex-1"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Continuer
        </Button>
      </div>

      <button
        onClick={sendVerificationCode}
        className="text-sm text-slate-500 hover:text-slate-700 underline w-full text-center"
        disabled={loading}
      >
        Renvoyer le code
      </button>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center gap-2">
        <CheckCircle className="w-5 h-5 text-green-600" />
        <span className="text-sm text-green-700 font-medium">Authentification vérifiée</span>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pin" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Code PIN (4 chiffres)
          </Label>
          <Input
            id="pin"
            type="password"
            value={newSettings.admin_pin}
            onChange={(e) => setNewSettings({ ...newSettings, admin_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            placeholder="Nouveau PIN (laisser vide pour ne pas changer)"
            maxLength={4}
            className="font-mono"
          />
          <p className="text-xs text-slate-500">PIN actuel : ****</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Numéro de téléphone
          </Label>
          <Input
            id="phone"
            type="tel"
            value={newSettings.admin_phone}
            onChange={(e) => setNewSettings({ ...newSettings, admin_phone: e.target.value })}
            placeholder="06 XX XX XX XX"
          />
          <p className="text-xs text-slate-500">Utilisé pour la vérification SMS</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email administrateur
          </Label>
          <Input
            id="email"
            type="email"
            value={newSettings.admin_email}
            onChange={(e) => setNewSettings({ ...newSettings, admin_email: e.target.value })}
            placeholder="admin@example.com"
          />
          <p className="text-xs text-slate-500">Utilisé pour la vérification et les notifications</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Annuler
        </Button>
        <Button
          onClick={saveSettings}
          disabled={loading}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Enregistrer
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-settings-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-slate-600" />
            Paramètres de Sécurité
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {step === 'verify' && renderVerification()}
          {step === 'settings' && renderSettings()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminSettingsModal;
